import {
	Add as AddIcon,
	Cancel as CancelIcon,
	Delete as DeleteIcon,
	InfoOutlined as InfoOutlinedIcon,
	KeyboardArrowLeft as KeyboardArrowLeftIcon,
	KeyboardArrowRight as KeyboardArrowRightIcon,
	Save as SaveIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Grid2, IconButton, Step, StepLabel, Stepper, TextField, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { IJurisdictionDetails, IRTODetails } from ".";
import { AGDataGrid } from "../../components/AGDataGrid";
import { AGDialog } from "../../components/AGDialog";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { useAppSelector } from "../../redux";
import {
	useCreateJurisdictionMutation,
	useDeleteJurisdictionMutation,
	useEditJurisdictionMutation,
	useGetJurisdictionsListQuery
} from "../../redux/reducers/jurisdictions.reducer";
import { JURISDICTION_TYPE_E } from "../../types";
import { DEFAULT_JURISDICTION_PARENT } from "../../utils/constants";
import RTODetailsDialog from "./RTODetailsDialog";
import { useNotification } from "../../components/FeedbackNotification/useNotification";

interface IJurisdictionDetailsDialogProps {
	open: boolean;
	jurisdictionDetails: IJurisdictionDetails | null;
	onClose: () => void;
}

interface IJurisdictionFormData {
	name: string;
	description: string;
}

enum SELECTED_TAB_E {
	JURISDICTION,
	RTO
}

const JurisdictionDetailsDialog: FC<IJurisdictionDetailsDialogProps> = (props) => {
	const { open, jurisdictionDetails, onClose } = props;

	const authState = useAppSelector((state) => state.auth);

	const { notify } = useNotification();

	const [selectedTab, setSelectedTab] = useState<SELECTED_TAB_E>(SELECTED_TAB_E.JURISDICTION);
	const [rowsData, setRowsData] = useState<IRTODetails[]>([]);
	const [rtoDetailsDialogOpen, setRTODetailsDialogOpen] = useState<boolean>(false);
	const [selectedRTODetails, setSelectedRTODetails] = useState<IRTODetails | null>(null);
	const [selectedJurisdictionDetails, setSelectedJurisdictionDetails] = useState<IJurisdictionDetails | null>(null);
	const [apiError, setApiError] = useState<string>("");
	const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);

	// APIS
	// GET JURISDICTIONS LIST
	const {
		data: getJurisdictionsListResponse,
		isLoading: getJurisdictionsListLoading,
		isFetching: getJurisdictionsListFetching,
		refetch: refetchJurisdictionsList
	} = useGetJurisdictionsListQuery(
		{
			page: 1,
			jurisdiction_type: JURISDICTION_TYPE_E.RTOS,
			parent: selectedJurisdictionDetails?.id
		},
		{ skip: !selectedJurisdictionDetails }
	);

	// CREATE JURISDICTION
	const [createJurisdiction, { isLoading: createJurisdictionLoading }] = useCreateJurisdictionMutation();

	// EDIT JURISDICTION
	const [editJurisdiction, { isLoading: editJurisdictionLoading }] = useEditJurisdictionMutation();

	// DELETE RTO
	const [deleteRTO, { isLoading: deleteRTOLoading, reset: resetDeleteRTOState }] = useDeleteJurisdictionMutation();

	const readOnlyInput = useMemo<boolean>(
		() => !authState.user.permissions || !authState.user.permissions.rto_metadata?.edit,
		[authState.user.permissions]
	);

	const {
		reset,
		register,
		handleSubmit,
		formState: { errors, isDirty: inputChanged }
	} = useForm<IJurisdictionFormData>();

	function handleChangeTab(tab: SELECTED_TAB_E) {
		setSelectedTab(tab);
	}

	function handleOpenRTODetailsDialog(rtoDetails: IRTODetails | null) {
		setSelectedRTODetails(rtoDetails);
		setRTODetailsDialogOpen(true);
	}

	function handleCloseRTODetailsDialog() {
		setRTODetailsDialogOpen(false);
		setSelectedRTODetails(null);
		refetchJurisdictionsList();
	}

	function handlePreviousButtonClick() {
		setSelectedTab(SELECTED_TAB_E.JURISDICTION);
	}

	function openDeleteConfirmationDialog(rtoDetails: IRTODetails) {
		setSelectedRTODetails(rtoDetails);
		setShowDeleteConfirmationDialog(true);
	}

	function closeDeleteConfirmationDialog() {
		setShowDeleteConfirmationDialog(false);
		setSelectedRTODetails(null);
	}

	function handleDeleteRTO() {
		if (!selectedRTODetails) return;

		deleteRTO(selectedRTODetails.id)
			.unwrap()
			.then(() => {
				refetchJurisdictionsList();
			})
			.catch((error) => {
				notify("data" in error && error.data ? String(error.data) : "Failed to delete RTO", "error");
			})
			.finally(() => {
				closeDeleteConfirmationDialog();
				resetDeleteRTOState();
			});
	}

	function handleSubmitForm(formData: IJurisdictionFormData) {
		if (selectedJurisdictionDetails) {
			// EDIT JURISDICTION
			editJurisdiction({
				id: selectedJurisdictionDetails.id,
				data: {
					name: formData.name,
					jurisdiction_type: JURISDICTION_TYPE_E.JURISDICTION,
					parent: DEFAULT_JURISDICTION_PARENT,
					description: formData.description
				}
			})
				.unwrap()
				.then(() => {
					setApiError("");
					if (!jurisdictionDetails) setSelectedTab(SELECTED_TAB_E.RTO);
					else {
						notify("Jurisdiction details updated successfully", "success");
					}
				})
				.catch((error) => {
					setApiError("data" in error && error.data ? String(error.data) : "Failed to save jurisdiction details");
				});
		} else {
			// CREATE JURISDICTION
			createJurisdiction({
				name: formData.name,
				jurisdiction_type: JURISDICTION_TYPE_E.JURISDICTION,
				parent: DEFAULT_JURISDICTION_PARENT,
				description: formData.description
			})
				.unwrap()
				.then((response) => {
					setApiError("");
					setSelectedJurisdictionDetails({
						id: response.id ?? 0,
						sr_no: -1,
						name: response.name ?? "",
						description: response.description ?? "",
						rto_codes: []
					});
					if (!jurisdictionDetails) setSelectedTab(SELECTED_TAB_E.RTO);
				})
				.catch((error) => {
					setApiError("data" in error && error.data ? String(error.data) : "Failed to save jurisdiction details");
				});
		}
	}

	useEffect(() => {
		if (open) {
			reset({
				name: jurisdictionDetails?.name ?? "",
				description: jurisdictionDetails?.description ?? ""
			});
			setSelectedTab(SELECTED_TAB_E.JURISDICTION);
			setSelectedJurisdictionDetails(jurisdictionDetails);
			setApiError("");
		} else {
			setRowsData([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jurisdictionDetails, open]);

	useEffect(() => {
		if (getJurisdictionsListResponse) {
			setRowsData(
				getJurisdictionsListResponse.results.map((item, index) => ({
					id: item.id,
					sr_no: index + 1,
					name: item.name,
					rto_code: item.rto_code || "",
					description: item.description,
					jurisdiction: item.parent
				}))
			);
		}
	}, [getJurisdictionsListResponse]);

	const columnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 65,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "rto_code",
			headerName: "RTO Code",
			flex: 1,
			minWidth: 100,
			sortable: false
		},
		{
			field: "name",
			headerName: "RTO Name",
			flex: 2,
			minWidth: 100,
			sortable: false
		},
		{
			field: "description",
			headerName: "Description",
			flex: 2,
			minWidth: 100,
			sortable: false
		},
		{
			field: "actions",
			headerName: "",
			width: readOnlyInput ? 50 : 75,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IRTODetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					{!readOnlyInput ? (
						<IconButton color="error" size="small" onClick={() => openDeleteConfirmationDialog(params.row)}>
							<DeleteIcon fontSize="inherit" />
						</IconButton>
					) : null}

					<IconButton color="info" size="small" onClick={() => handleOpenRTODetailsDialog(params.row)}>
						<InfoOutlinedIcon fontSize="inherit" />
					</IconButton>
				</Box>
			)
		}
	];

	const TabRenderer: { [key in SELECTED_TAB_E]: ReactNode } = {
		[SELECTED_TAB_E.JURISDICTION]: (
			<Box component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12 }}>
						<TextField
							fullWidth
							size="small"
							label="Jurisdiction Name"
							autoComplete="off"
							error={!!errors.name}
							helperText={errors.name?.message?.toString() || ""}
							slotProps={{ input: { readOnly: readOnlyInput } }}
							{...register("name", {
								required: "Jurisdiction name is required",
								setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
							})}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<TextField
							fullWidth
							multiline
							minRows={3}
							size="small"
							label="Description"
							autoComplete="off"
							error={!!errors.description}
							helperText={errors.description?.message?.toString() || ""}
							slotProps={{ input: { readOnly: readOnlyInput } }}
							{...register("description", {
								setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
							})}
						/>
					</Grid2>
				</Grid2>

				{apiError ? (
					<Typography variant="body2" color="error" textAlign="center" marginTop="1rem">
						{apiError}
					</Typography>
				) : null}

				{!readOnlyInput ? (
					jurisdictionDetails ? (
						<Box className="jurisdiction-dialog-footer">
							<Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={onClose}>
								Cancel
							</Button>

							<LoadingButton
								disableElevation
								variant="contained"
								color="primary"
								type="submit"
								startIcon={<SaveIcon />}
								loading={createJurisdictionLoading || editJurisdictionLoading}
								loadingPosition="start"
								disabled={!inputChanged}
							>
								Save
							</LoadingButton>
						</Box>
					) : (
						<Box className="jurisdiction-dialog-footer space-between">
							<Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={onClose}>
								Cancel
							</Button>

							<LoadingButton
								disableElevation
								variant="contained"
								color="primary"
								type="submit"
								endIcon={<KeyboardArrowRightIcon />}
								loading={createJurisdictionLoading || editJurisdictionLoading}
							>
								Save & Next
							</LoadingButton>
						</Box>
					)
				) : null}
			</Box>
		),
		[SELECTED_TAB_E.RTO]: (
			<>
				{!readOnlyInput ? (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: "0.5rem" }}>
						<Button
							disableElevation
							variant="contained"
							color="primary"
							startIcon={<AddIcon />}
							onClick={() => handleOpenRTODetailsDialog(null)}
						>
							Add RTO
						</Button>
					</Box>
				) : null}

				<AGDataGrid
					hideFooter
					hideLastSeparator
					columns={columnsData}
					rows={rowsData}
					loading={getJurisdictionsListLoading || getJurisdictionsListFetching}
				/>

				{!readOnlyInput && !jurisdictionDetails ? (
					<Box className="jurisdiction-dialog-footer space-between">
						<Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={onClose}>
							Cancel
						</Button>

						<Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
							<Button
								variant="outlined"
								color="primary"
								startIcon={<KeyboardArrowLeftIcon />}
								onClick={handlePreviousButtonClick}
							>
								Previous
							</Button>

							<LoadingButton disableElevation variant="contained" color="primary" onClick={onClose}>
								Done
							</LoadingButton>
						</Box>
					</Box>
				) : null}
			</>
		)
	};

	return (
		<AGDialog open={open} onClose={onClose} title="Jurisdiction" color="primary" maxWidth="sm">
			{jurisdictionDetails ? (
				<Box className="tabs-wrapper">
					<Box
						className={`tab-item-wrapper ${selectedTab === SELECTED_TAB_E.JURISDICTION ? "selected" : ""}`}
						onClick={() => handleChangeTab(SELECTED_TAB_E.JURISDICTION)}
					>
						<Typography textAlign="center">Jurisdiction Details</Typography>
					</Box>

					<Box
						className={`tab-item-wrapper ${selectedTab === SELECTED_TAB_E.RTO ? "selected" : ""}`}
						onClick={() => handleChangeTab(SELECTED_TAB_E.RTO)}
					>
						<Typography textAlign="center">RTO Details</Typography>
					</Box>
				</Box>
			) : (
				<Box sx={{ marginBottom: "1.5rem" }}>
					<Stepper activeStep={selectedTab === SELECTED_TAB_E.JURISDICTION ? 0 : 1}>
						<Step completed={selectedTab === SELECTED_TAB_E.RTO}>
							<StepLabel>Jurisdiction Details</StepLabel>
						</Step>

						<Step>
							<StepLabel>RTO Details</StepLabel>
						</Step>
					</Stepper>
				</Box>
			)}

			<Box>{TabRenderer[selectedTab]}</Box>

			<RTODetailsDialog
				open={rtoDetailsDialogOpen}
				rtoDetails={selectedRTODetails}
				jurisdictionId={selectedJurisdictionDetails?.id ?? -1}
				onClose={handleCloseRTODetailsDialog}
			/>

			<ConfirmationDialog
				open={showDeleteConfirmationDialog}
				title="Delete RTO"
				heading="Are you sure you want to delete this RTO?"
				onClose={closeDeleteConfirmationDialog}
				onConfirm={handleDeleteRTO}
				color="error"
				confirmButtonLoading={deleteRTOLoading}
				submitButtonText="Delete"
				width="xs"
			/>
		</AGDialog>
	);
};

export default JurisdictionDetailsDialog;
